import { Link, NavLink } from "react-router-dom";
import { useState, useEffect } from "react";
import { BRAND, PRIMARY_NAV } from "@/lib/brand";
import { Menu, X } from "lucide-react";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header data-testid="main-header" className={`w-full bg-white sticky top-0 z-40 transition-shadow duration-300 ${scrolled ? "shadow-md border-b border-slate-200" : "border-b border-slate-100"}`}>
      {/* Federation title block */}
      <div className="nkf-container py-2.5 sm:py-3 md:py-4">
        <div className="flex items-center gap-2.5 sm:gap-3 md:gap-5">
          <Link to="/" data-testid="brand-logo" className="shrink-0 group">
            <img
              src={BRAND.logo}
              alt="NKF Bharat"
              className="h-12 w-12 sm:h-14 sm:w-14 md:h-20 md:w-20 object-contain rounded-full transition-transform duration-500 group-hover:rotate-[8deg] group-hover:scale-105"
            />
          </Link>
          <div className="min-w-0 flex-1">
            <h1 className="font-display text-nkf-red text-[15px] leading-[1.15] sm:text-lg md:text-2xl lg:text-3xl font-bold tracking-tight">
              {BRAND.main}
            </h1>
            <div className="hindi text-nkf-green text-[12px] sm:text-base md:text-lg leading-snug mt-0.5">{BRAND.sub}</div>
            <div className="text-[10px] sm:text-[11px] md:text-sm text-slate-600 mt-0.5 leading-snug hidden sm:block">
              {BRAND.mission}
            </div>
            <div className="mt-1 hidden md:flex flex-wrap items-center gap-x-3 gap-y-1 text-xs lg:text-sm font-semibold">
              <span className="text-nkf-blue">Traditional Karate</span>
              <span className="text-nkf-blue/40">|</span>
              <span className="text-nkf-blue">Semi Contact Karate</span>
              <span className="text-nkf-blue/40">|</span>
              <span className="text-nkf-blue">Full Contact Karate</span>
            </div>
          </div>
          {/* Right-side seal */}
          <div className="hidden lg:flex flex-col items-end shrink-0">
            <div className="px-3 py-1 rounded-full bg-nkf-gold/15 text-nkf-blue text-[11px] font-semibold tracking-widest uppercase border border-nkf-gold/40">
              Official Federation Portal
            </div>
            <div className="text-[11px] text-slate-500 mt-1">{BRAND.statement}</div>
          </div>
        </div>
        {/* Mobile-only mission line */}
        <div className="mt-1.5 sm:hidden text-[11px] text-slate-600 leading-snug">
          {BRAND.mission}
        </div>
        {/* Disciplines strip on mobile */}
        <div className="mt-1.5 md:hidden flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[10.5px] sm:text-[11px] font-semibold">
          <span className="text-nkf-blue">Traditional Karate</span>
          <span className="text-nkf-blue/40">|</span>
          <span className="text-nkf-blue">Semi Contact Karate</span>
          <span className="text-nkf-blue/40">|</span>
          <span className="text-nkf-blue">Full Contact Karate</span>
        </div>
      </div>

      {/* Primary nav */}
      <nav className="border-t border-slate-200 bg-nkf-blue text-white">
        <div className="nkf-container flex items-center justify-between">
          <ul className="hidden md:flex items-center flex-wrap" data-testid="primary-nav">
            {PRIMARY_NAV.map((n) => (
              <li key={n.to}>
                <NavLink
                  to={n.to}
                  end={n.to === "/"}
                  data-testid={`nav-${n.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `relative px-4 lg:px-5 py-3 text-[12px] lg:text-[13px] uppercase tracking-widest font-semibold inline-block transition-colors duration-300 ${
                      isActive ? "text-nkf-gold" : "text-white/85 hover:text-nkf-gold"
                    } after:content-[''] after:absolute after:left-3 after:right-3 after:bottom-1.5 after:h-[2px] after:bg-nkf-gold after:scale-x-0 after:origin-left after:transition-transform after:duration-300 hover:after:scale-x-100 ${isActive ? "after:scale-x-100" : ""}`
                  }
                >
                  {n.label}
                </NavLink>
              </li>
            ))}
          </ul>
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden p-3 text-white hover:text-nkf-gold transition"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
        <div className={`md:hidden bg-nkf-navy overflow-hidden transition-[max-height] duration-500 ${open ? "max-h-[600px]" : "max-h-0"}`}>
          <ul>
            {PRIMARY_NAV.map((n, i) => (
              <li key={n.to} className="animate-fade-up" style={{ animationDelay: `${i * 35}ms` }}>
                <NavLink
                  to={n.to}
                  end={n.to === "/"}
                  onClick={() => setOpen(false)}
                  className="block px-5 py-3 text-sm uppercase tracking-widest font-semibold border-t border-white/10 text-white/90 hover:text-nkf-gold transition"
                >
                  {n.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      </nav>
    </header>
  );
}
